import * as React from 'react';
import { Filespace, FilespaceStorage } from 'fs-api-client';
import FilespaceCard, { CardType } from './common/FilespaceCard';
import { InitializeFilespaceModal } from './common/modals/InitializeFilespaceModal';
import { DeleteFilespaceModal } from './common/modals/DeleteFilespaceModal';
import { Link } from 'react-router-dom';
import { DomainInjectorProperties, injectDomain } from './hocs/DomainInjector';
import { EventManager } from '../services/EventManager';
import { getRegionName } from '../utils/utilFunctions';
import { PageRoutes } from './root/Routes';
import SubscriptionStatusAlert from './common/SubscriptionStatusAlert';
import { injectProfile, ProfileInjectorProperties } from './hocs/ProfileInjector';
import { BillingPeriodInjectorProperties, injectBillingPeriod } from './hocs/BillingPeriodInjector';
import { EventHandler } from './hocs/EventHandler';
import { suspendedErrorMessage, suspendedErrorTitle, IS_BLEEDING_EDGE } from '../utils/constants';
import { Message } from './common/Message';
import { WebportalEvent } from '../services/WebportalEvent';
import { injectTransitionTypeHandlers } from './hocs/transitionType/TransitionTypeInjector';
import { withMapBackground } from './hocs/MapBackgroundEnhancer';
import SuspensionStatusEnum = Filespace.SuspensionStatusEnum;
import RenameStatusEnum = Filespace.RenameStatusEnum;
import { RenameFilespaceModal } from './common/modals/RenameFilespaceModal';
import { FilespaceDetailsModal } from './common/modals/FilespaceDetailsModal';
import { updateMarketingDataAttributesService } from '../services/MarketingDataAttributesSetter';

const DISABLE_FILESPACE_CREATION = process.env.DISABLE_FILESPACE_CREATION || false;

const TOGGLE_ALL_CARDS_KEY_CODE = 222; // This is '''

interface DashboardProperties extends DomainInjectorProperties,
    ProfileInjectorProperties,
    BillingPeriodInjectorProperties {
    isUserSuspended: boolean;
}

interface DashboardState {
    selectedFilespace: Filespace;
    showAll: boolean;
}

let dashboardIsMounted = false;
const getDashboardIsMounted = () => {
    return dashboardIsMounted;
};

class Dashboard extends React.Component<DashboardProperties, DashboardState> {
    private readonly WORKSPACE_REFRESH_INTERVAL_MS: number = 10_000;
    private interval: NodeJS.Timer;
    private mockedFilespaceIndex: number = 0;

    constructor(props: any) {
        super(props);
        this.createNewFilespace = this.createNewFilespace.bind(this);
        this.onFilespaceDeletedSuccessfully = this.onFilespaceDeletedSuccessfully.bind(this);
        this.renderFilespaces = this.renderFilespaces.bind(this);
        this.determineProvisionedCardType = this.determineProvisionedCardType.bind(this);
        this.determineInitializedCardType = this.determineInitializedCardType.bind(this);

        this.state = {
            selectedFilespace: new Filespace(),
            showAll: false
        };
    }

    render() {
        return (
            <div className="container dashboard">
                <SubscriptionStatusAlert history={this.props.history}/>
                {this.renderFilespaces()}
            </div>
        );
    }

    componentDidMount() {
        EventManager.fireCustomEvent(WebportalEvent.OrderFlexCards);
        EventHandler.onDashboardEnter();
        this.initializeInterval();
        dashboardIsMounted = true;

        updateMarketingDataAttributesService.updateUserAttributes();

        document.addEventListener('keyup', (e) => {
            if (!IS_BLEEDING_EDGE) {
                return;
            }

            switch (e.keyCode) {
                case TOGGLE_ALL_CARDS_KEY_CODE:
                    this.setState({ showAll: !this.state.showAll });
                    break;
                default:
                    break;
            }
        });
    }

    componentDidUpdate() {
        EventManager.fireCustomEvent(WebportalEvent.OrderFlexCards);
    }

    componentWillUnmount() {
        clearInterval(this.interval);
        dashboardIsMounted = false;
        EventHandler.onDashboardExit();
    }

    private mockFilespace(
        provider: FilespaceStorage.ProviderEnum,
        status: Filespace.StatusEnum,
        isLucidLinkProvided: boolean,
        isSandbox: boolean,
        subscriptionType: Filespace.SubscriptionTypeEnum,
        version: number = 3,
        suspensionStatus: SuspensionStatusEnum = Filespace.SuspensionStatusEnum.NotSuspended,
        renameStatus: RenameStatusEnum | undefined = undefined,
    ): Filespace {
        this.mockedFilespaceIndex++;
        return {
            domain: 'testov',
            name: `${provider}-${status}-v${version}-${this.mockedFilespaceIndex}`.toLowerCase(),
            status,
            renameStatus,
            version: version,
            suspensionStatus: suspensionStatus,
            migrationStatus: Filespace.MigrationStatusEnum.NotMigrated,
            type: isSandbox ? Filespace.TypeEnum.Sandbox : Filespace.TypeEnum.Unlimited,
            subscriptionType,
            storage: {
                owner: isLucidLinkProvided ?
                    FilespaceStorage.OwnerEnum.Lucidlink :
                    FilespaceStorage.OwnerEnum.Customer,
                provider
            },
            stats: {
                connectedDevices: 0,
                deleteRequestCount: 874844,
                filespaceSize: 4028123241,
                usedStorage: 3028123242,
                getDataSize: 85841742819,
                getRequestCount: 894998,
                listRequestCount: 0,
                putDataSize: 131913610288,
                putRequestCount: 908030
            }
        };
    }

    private mockAllFilespaces(): Filespace[] {
        this.mockedFilespaceIndex = 0;

        const result: Filespace[] = [];

        const Provider = FilespaceStorage.ProviderEnum;
        const Status = Filespace.StatusEnum;
        const Plan = Filespace.SubscriptionTypeEnum;
        const SuspensionStatus = SuspensionStatusEnum;

        // Provisioning

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioning, true, false, Filespace.SubscriptionTypeEnum.Enterprise, 3));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioning, true, false, Filespace.SubscriptionTypeEnum.Enterprise, 4));

        // Rename

        result.push(this.mockFilespace(Provider.Wasabi, Status.Initialized, true, false, Plan.Basic, 3, SuspensionStatusEnum.NotSuspended, RenameStatusEnum.Inprogress));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Initialized, true, false, Plan.Basic, 3, SuspensionStatusEnum.NotSuspended, RenameStatusEnum.Errored));

        result.push(this.mockFilespace(Provider.Wasabi, Status.Initialized, true, false, Plan.Basic, 4, SuspensionStatusEnum.NotSuspended, RenameStatusEnum.Inprogress));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Initialized, true, false, Plan.Basic, 4, SuspensionStatusEnum.NotSuspended, RenameStatusEnum.Errored));

        // Suspended and suspending

        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 3, SuspensionStatus.Suspended));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 4, SuspensionStatus.Suspended));

        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 3, SuspensionStatus.Suspending));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 4, SuspensionStatus.Suspending));

        // AWS sandbox

        // LUCIDLINK PROVIDED

        //     Wasabi
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Initialized, true, false, Plan.Basic));

        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 4));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Initialized, true, false, Plan.Basic, 4));

        //     IBM
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Teams));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Teams));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Teams, 4));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Teams, 4));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Enterprise));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Enterprise));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Advanced));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Advanced));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Enterprise, 4));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Enterprise, 4));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Advanced, 4));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Advanced, 4));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Enterprise, 3, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Advanced, 3, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Teams, 3, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, false, false, Plan.Custom, 3, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 3, SuspensionStatusEnum.Errored));

        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Enterprise, 3, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Advanced, 3, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, true, false, Plan.Teams, 3, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, false, false, Plan.Custom, 3, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Initialized, true, false, Plan.Basic, 3, SuspensionStatusEnum.Errored));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Enterprise, 4, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Advanced, 4, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Teams, 4, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, false, false, Plan.Custom, 4, SuspensionStatusEnum.Errored));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 4, SuspensionStatusEnum.Errored));

        result.push(this.mockFilespace(Provider.IBM, Status.Errored, true, false, Plan.Enterprise));
        result.push(this.mockFilespace(Provider.IBM, Status.Errored, true, false, Plan.Advanced));
        result.push(this.mockFilespace(Provider.IBM, Status.Errored, true, false, Plan.Teams));
        result.push(this.mockFilespace(Provider.IBM, Status.Errored, false, false, Plan.Custom));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Errored, true, false, Plan.Basic));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Enterprise, 3, SuspensionStatusEnum.UnsuspensionFailed));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Advanced, 3, SuspensionStatusEnum.UnsuspensionFailed));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Teams, 3, SuspensionStatusEnum.UnsuspensionFailed));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, false, false, Plan.Custom, 3, SuspensionStatusEnum.UnsuspensionFailed));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 3, SuspensionStatusEnum.UnsuspensionFailed));

        result.push(this.mockFilespace(Provider.IBM, Status.Errored, true, false, Plan.Enterprise, 4));
        result.push(this.mockFilespace(Provider.IBM, Status.Errored, true, false, Plan.Advanced, 4));
        result.push(this.mockFilespace(Provider.IBM, Status.Errored, true, false, Plan.Teams, 4));
        result.push(this.mockFilespace(Provider.IBM, Status.Errored, false, false, Plan.Custom, 4));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Errored, true, false, Plan.Basic, 4));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Enterprise, 4, SuspensionStatusEnum.UnsuspensionFailed));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Advanced, 4, SuspensionStatusEnum.UnsuspensionFailed));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Teams, 4, SuspensionStatusEnum.UnsuspensionFailed));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, false, false, Plan.Custom, 4, SuspensionStatusEnum.UnsuspensionFailed));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 4, SuspensionStatusEnum.UnsuspensionFailed));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Enterprise, 4, SuspensionStatusEnum.Suspended));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Advanced, 4, SuspensionStatusEnum.Suspended));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, true, false, Plan.Teams, 4, SuspensionStatusEnum.Suspended));
        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, false, false, Plan.Custom, 4, SuspensionStatusEnum.Suspended));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Plan.Basic, 4, SuspensionStatusEnum.Suspended));

        result.push(this.mockFilespace(Provider.IBM, Status.Provisioned, false, false, Plan.Custom));
        result.push(this.mockFilespace(Provider.IBM, Status.Initialized, false, false, Plan.Custom));

        result.push(this.mockFilespace(Provider.AWS, Status.Provisioned, false, false, Plan.Custom, 4));
        result.push(this.mockFilespace(Provider.AWS, Status.Initialized, false, false, Plan.Custom, 4));

        // LEGACY

        //     Wasabi
        result.push(this.mockFilespace(Provider.Wasabi, Status.Provisioned, true, false, Filespace.SubscriptionTypeEnum.None));
        result.push(this.mockFilespace(Provider.Wasabi, Status.Initialized, true, false, Filespace.SubscriptionTypeEnum.None));

        //     AWS
        result.push(this.mockFilespace(Provider.AWS, Status.Provisioned, true, true, Filespace.SubscriptionTypeEnum.None));
        result.push(this.mockFilespace(Provider.AWS, Status.Initialized, true, true, Filespace.SubscriptionTypeEnum.None));

        result.push(this.mockFilespace(Provider.AWS, Status.Provisioned, true, false, Filespace.SubscriptionTypeEnum.None));
        result.push(this.mockFilespace(Provider.AWS, Status.Initialized, true, false, Filespace.SubscriptionTypeEnum.None));

        return result;
    }

    private renderFilespaces() {
        let filespaces: Filespace[] = this.props.filespaces,
            cards: JSX.Element | null = null;
        const userEmail = DISABLE_FILESPACE_CREATION ?
            this.props.email :
            '';
        const filespacesToRender = this.state.showAll ? this.mockAllFilespaces() : filespaces;

        if (filespacesToRender && filespacesToRender.length >= 1) {
            let existingFilespaces = filespacesToRender.map(filespace => {
                const isSuspended = filespace.suspensionStatus !== Filespace.SuspensionStatusEnum.NotSuspended;
                if (filespace.status === Filespace.StatusEnum.Provisioning) {
                    return (
                        <FilespaceCard
                            transitionType={this.props.transitionType}
                            isFilespaceSuspended={isSuspended}
                            isUserSuspended={this.props.isUserSuspended}
                            key={filespace.name}
                            cardType={CardType.Provisioning}
                            domainName={this.props.domain.name}
                            userEmail={userEmail!}
                            filespace={filespace}
                        />
                    );
                } else if (filespace.status === Filespace.StatusEnum.Errored) {
                    const cardType = this.determineProvisionedCardType(filespace);
                    return (
                        <FilespaceCard
                            transitionType={this.props.transitionType}
                            isFilespaceSuspended={isSuspended}
                            isUserSuspended={this.props.isUserSuspended}
                            key={filespace.name}
                            cardType={cardType}
                            domainName={this.props.domain.name}
                            region={getRegionName(filespace.storage.provider, filespace.storage.region!)}
                            provider={filespace.storage.provider}
                            onClick={() => {
                                this.selectFilespace(filespace);
                            }}
                            userEmail={userEmail!}
                            filespace={filespace}
                        />
                    );
                } else if (filespace.status === Filespace.StatusEnum.Provisioned) {
                    const cardType = this.determineProvisionedCardType(filespace);
                    return (
                        <FilespaceCard
                            transitionType={this.props.transitionType}
                            isFilespaceSuspended={isSuspended}
                            isUserSuspended={this.props.isUserSuspended}
                            key={filespace.name}
                            domainName={this.props.domain.name}
                            cardType={cardType}
                            region={getRegionName(filespace.storage.provider, filespace.storage.region!)}
                            provider={filespace.storage.provider}
                            onClick={() => {
                                this.selectFilespace(filespace);
                            }}
                            userEmail={userEmail!}
                            filespace={filespace}
                        />
                    );
                } else if (filespace.status === Filespace.StatusEnum.Initialized) {
                    const cardType = this.determineInitializedCardType(filespace);
                    return (
                            <FilespaceCard
                                transitionType={this.props.transitionType}
                                isFilespaceSuspended={isSuspended}
                                isUserSuspended={this.props.isUserSuspended}
                                key={filespace.name}
                                cardType={cardType}
                                domainName={this.props.domain.name}
                                region={getRegionName(filespace.storage.provider, filespace.storage.region!)}
                                provider={filespace.storage.provider}
                                connectedDevices={filespace.stats?.connectedDevices}
                                onClick={() => {
                                    this.selectFilespace(filespace);
                                }}
                                userEmail={userEmail!}
                                filespace={filespace}
                            />
                        );
                    } else {
                    return null;
                }
            });

            cards = (
                <React.Fragment>
                    {existingFilespaces}

                    <FilespaceCard
                        transitionType={this.props.transitionType}
                        isUserSuspended={this.props.isUserSuspended}
                        domainName={this.props.domain.name}
                        cardType={Boolean(this.props.domain.migrationStartedAt) && process.env.REACT_APP_ENV_NAME !== 'be' ? CardType.DomainMigrationStarted : CardType.CreateNew}
                        onClick={this.createNewFilespace}
                        userEmail={userEmail!}
                        filespace={null}
                    />
                </React.Fragment>
            );
        } else {
            cards = (
                <FilespaceCard
                    transitionType={this.props.transitionType}
                    isUserSuspended={this.props.isUserSuspended}
                    domainName={this.props.domain.name}
                    cardType={CardType.CreateFirst}
                    onClick={this.createNewFilespace}
                    userEmail={userEmail!}
                    filespace={null}
                />
            );
        }

        return (
            <div className="row">
                <main className="col">
                    <h1 className="title">
                        Filespaces in your domain &nbsp;
                        <Link
                            to={PageRoutes.EditDomain + PageRoutes.Dashboard}
                            className="edit"
                        >
                            {this.props.domain.name}
                        </Link>
                    </h1>
                    {!this.props.isUserSuspended && (
                        <p className="subtitle">Welcome! You can create and manage your Filespaces here.</p>)}
                    {this.props.isUserSuspended && <Message
                        title={suspendedErrorTitle}
                        message={suspendedErrorMessage}
                        button={{
                            text: 'Go to billing',
                            href: '/billing'
                        }}
                    />}

                    <InitializeFilespaceModal
                        filespace={this.state.selectedFilespace}
                        domainName={this.props.domain.name}
                    />
                    <RenameFilespaceModal
                        filespace={this.state.selectedFilespace}
                        domainName={this.props.domain.name}
                        userEmail={this.props.email}
                    />
                    <DeleteFilespaceModal
                        domain={this.props.domain}
                        filespace={this.state.selectedFilespace}
                        onFilespaceDeleted={this.onFilespaceDeletedSuccessfully}
                        onFilespaceDelete={() => {
                            this.props.showLoader(() => { /* */
                            });
                        }}
                    />
                    <FilespaceDetailsModal
                        filespace={this.state.selectedFilespace}
                        domainName={this.props.domain.name}
                    />
                    <div className="row cards-container">
                        {cards}
                    </div>
                </main>
            </div>
        );
    }

    private onFilespaceDeletedSuccessfully() {
        this.props.fetchDomain(true);
    }

    private createNewFilespace() {
        this.props.history.push(PageRoutes.CreateFilespace);
    }

    private determineProvisionedCardType(filespace: Filespace): CardType {
        if (filespace.storage.owner !== FilespaceStorage.OwnerEnum.Lucidlink) {
            return CardType.CustomerUninitialized;
        }

        let cardType;
        if (filespace.storage.provider === FilespaceStorage.ProviderEnum.AWS) {
            if (filespace.type === Filespace.TypeEnum.Sandbox) {
                cardType = CardType.SandboxUninitialized;
                return cardType;
            } else {
                if (filespace.subscriptionType === Filespace.SubscriptionTypeEnum.Advanced) {
                    cardType = CardType.LucidLinkAWSAdvancedUninitialized;
                } else {
                    cardType = CardType.LucidLinkAWSUninitialized;
                }
                return cardType; // @TODO add check for sandbox
            }
        } else if (filespace.storage.provider === FilespaceStorage.ProviderEnum.IBM) {
            if (filespace.subscriptionType === Filespace.SubscriptionTypeEnum.Teams) {
                return CardType.LucidLinkIBMTeamsUninitialized;
            } else if (filespace.subscriptionType === Filespace.SubscriptionTypeEnum.Enterprise) {
                return CardType.LucidLinkIBMEnterpriseUninitialized;
            } else {
                return CardType.LucidLinkIBMAdvancedUninitialized;
            }
        } else {
            cardType = CardType.LucidLinkWasabiUninitialized;
            return cardType;
        }
    }

    private determineInitializedCardType(filespace: Filespace): CardType {
        if (filespace.renameStatus === Filespace.RenameStatusEnum.Inprogress) {
            return CardType.Renaming;
        }

        if (filespace.storage.owner !== FilespaceStorage.OwnerEnum.Lucidlink) {
            return CardType.Customer;
        }

        let cardType;
        if (filespace.storage.provider === FilespaceStorage.ProviderEnum.AWS) {
            if (filespace.type === Filespace.TypeEnum.Sandbox) {
                cardType = CardType.Sandbox;
                return cardType;
            } else {
                if (filespace.subscriptionType === Filespace.SubscriptionTypeEnum.Advanced) {
                    cardType = CardType.LucidLinkAWSAdvanced;
                } else {
                    cardType = CardType.LucidLinkAWS;
                }
                return cardType; // @TODO add check for sandbox
            }
        } else if (filespace.storage.provider === FilespaceStorage.ProviderEnum.IBM) {
            if (filespace.subscriptionType === Filespace.SubscriptionTypeEnum.Teams) {
                return CardType.LucidLinkIBMTeams;
            } else if (filespace.subscriptionType === Filespace.SubscriptionTypeEnum.Enterprise) {
                return CardType.LucidLinkIBMEnterprise;
            } else {
                return CardType.LucidLinkIBMAdvanced;
            }
        } else {
            cardType = CardType.LucidLinkWasabi;
            return cardType;
        }
    }

    private initializeInterval(): void {
        if (this.interval) {
            return;
        }

        this.interval = setInterval(() => this.props.fetchDomain(false), this.WORKSPACE_REFRESH_INTERVAL_MS);
    }

    private selectFilespace = (filespace: Filespace) => {
        this.setState({ selectedFilespace: filespace });
    }

}

export default injectBillingPeriod(
    injectProfile(
        injectDomain(
            injectTransitionTypeHandlers(
                withMapBackground(Dashboard),
            ) as any
        ) as any
    ) as any
);

export { getDashboardIsMounted };



// WEBPACK FOOTER //
// ./src/webportal/components/Dashboard.tsx